/**
 * One-way migration for menus published in an older shape.
 *
 * The griddle categories were once generated from bases plus topping upcharges
 * (`dosa`, then `dosaCategories`). They are now ordinary sections with
 * hand-typed prices, so this only reshapes docs still stored the old way. The
 * next publish writes the flat shape and this stops doing anything.
 *
 * Pure — no React, no Firestore.
 */

/** Ordered griddle sections, so a migrated menu keeps Dosa → Idly → Uttappam. */
const CATEGORY_SPEC = [
  // Pesarattu and Ragi Dosa are price *columns* of the Dosa matrix, not
  // sections — the three share one style list and differ only in price.
  { id: 'dosa', title: 'Dosa / Pesarattu / Ragi Dosa', bases: ['Dosa', 'Pesarattu', 'Ragi Dosa'] },
  { id: 'idly', title: 'Idly', bases: ['Idly', 'Idli'] },
  { id: 'uttappam', title: 'Uttappam', bases: ['Uthappam', 'Uttapam', 'Uttappam'] },
];

const NOTE = '✦ Served with sambar and chutneys · customize any of the above with add-ons';

const itemOf = (t) => {
  if (typeof t === 'string') return { name: t };
  const out = { name: t?.name || '' };
  if (t?.desc) out.desc = t.desc;
  if (t?.price != null) out.price = t.price;
  if (t?.img) out.img = t.img;
  return out;
};

/** Split a list of items into the two columns a section renders. */
const toColumns = (items) => {
  const half = Math.ceil(items.length / 2);
  return [items.slice(0, half), items.slice(half)];
};

const sectionFrom = ({ title, note, items, addons }) => ({
  title,
  ...(note ? { note } : {}),
  columns: toColumns(items),
  ...(addons?.veg?.length ? { addons: addons.veg } : {}),
  ...(addons?.nonveg?.length ? { nonvegAddons: addons.nonveg } : {}),
});

/** A `dosaCategories` entry → a plain section. */
export function categoryToSection(cat) {
  const items = (cat?.titles || []).map(itemOf).filter((i) => i.name);
  const combos = (cat?.combos || []).flatMap((g) => (g?.items || []).map(itemOf));
  return sectionFrom({
    title: cat?.title || CATEGORY_SPEC[0].title,
    note: cat?.note || NOTE,
    items: [...items, ...combos.filter((i) => i.name)],
    addons: cat?.addons,
  });
}

/** The pre-split single `dosa` key → up to three plain sections. */
export function splitLegacyDosa(dosa) {
  if (!dosa) return [];
  const { titles = [], note, addons = {} } = dosa;
  const claimed = new Set();

  const sections = CATEGORY_SPEC.map((spec) => {
    const items = titles
      .filter((t) => {
        const name = typeof t === 'string' ? t : t?.name;
        if (!name || !spec.bases.includes(name)) return false;
        claimed.add(name);
        return true;
      })
      .map(itemOf);
    return { spec, items };
  });

  // Anything the spec didn't claim stays with Dosa rather than being dropped.
  const leftovers = titles
    .filter((t) => {
      const name = typeof t === 'string' ? t : t?.name;
      return name && !claimed.has(name);
    })
    .map(itemOf);
  if (leftovers.length) sections[0].items.push(...leftovers);

  return sections
    .filter((s) => s.items.length)
    .map((s) => sectionFrom({
      title: s.spec.title, note: note || NOTE, items: s.items, addons,
    }));
}

/** Titles of the three griddle sections, in menu order. */
export const GRIDDLE_TITLES = CATEGORY_SPEC.map((c) => c.title);

/** True when a payload is still in one of the generated shapes. */
export function isLegacyShape(data) {
  return !!(data && (data.dosa || Array.isArray(data.dosaCategories)));
}

/**
 * Seed a migrated menu's griddle sections from the shipped JSON.
 *
 * Old docs stored only the base rows — combinations were computed at render —
 * so migration alone leaves those sections near-empty. Every other section is
 * left exactly as published, since those hold real edits.
 */
export function seedGriddleSections(migrated, seed) {
  if (!migrated?.sections || !seed?.sections) return migrated;
  const fromSeed = new Map(
    seed.sections.filter((s) => GRIDDLE_TITLES.includes(s.title)).map((s) => [s.title, s]),
  );
  if (!fromSeed.size) return migrated;

  // A section is either column-based or a price matrix; count whichever it is.
  const rows = (s) => (Array.isArray(s?.rows)
    ? s.rows.length
    : (s?.columns || []).flat().filter((i) => i?.name).length);
  let changed = false;

  // Fill in the griddle sections that exist but are near-empty.
  const sections = migrated.sections.map((s) => {
    const from = fromSeed.get(s.title);
    // Only fill in — never shrink a section the owner has built up.
    if (!from || rows(from) <= rows(s)) return s;
    changed = true;
    return from;
  });

  // Add griddle sections the old doc had no concept of.
  const present = new Set(sections.map((s) => s.title));
  const missing = GRIDDLE_TITLES
    .filter((t) => !present.has(t))
    .map((t) => fromSeed.get(t))
    .filter(Boolean);
  if (missing.length) changed = true;

  if (!changed) return migrated;

  // Keep the griddle block together and in menu order, ahead of the rest.
  const griddle = [...sections.filter((s) => GRIDDLE_TITLES.includes(s.title)), ...missing]
    .sort((a, b) => GRIDDLE_TITLES.indexOf(a.title) - GRIDDLE_TITLES.indexOf(b.title));
  const rest = sections.filter((s) => !GRIDDLE_TITLES.includes(s.title));
  return { ...migrated, sections: [...griddle, ...rest] };
}

/** Normalize any menu payload to the flat shape. Idempotent. */
export function normalizeMenuData(data) {
  if (!data || typeof data !== 'object') return data;
  const hasLegacy = !!data.dosa;
  const hasCategories = Array.isArray(data.dosaCategories);
  if (!hasLegacy && !hasCategories) return data;

  const migrated = hasCategories
    ? data.dosaCategories.map(categoryToSection)
    : splitLegacyDosa(data.dosa);

  // Keep the griddle sections ahead of the rest, where they've always rendered.
  const { dosa, dosaCategories, ...rest } = data;
  return { ...rest, sections: [...migrated, ...(data.sections || [])] };
}
